import { useEffect, useState } from "react";
import { Alert, Chip, CircularProgress, Grid, Icon, InputLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { errorAlert, infoAviso, successAlert } from "utils/Functions";
import { delNoResponse, get, post } from "utils/WebService";

// App do Microsoft 365 do escritório (Parametrização do Sistema > Microsoft 365): Tenant ID, Client ID e o segredo (este
// criptografado no banco), que nunca voltam para a tela, como as chaves da OpenAI (só o final; em branco = mantém o
// gravado), e a caixa dos e-mails do sistema.
// Usado pelos e-mails do sistema e dos robôs (saem por essa caixa, sem SMTP), pela Mala Direta (conta de saída do tipo
// Microsoft 365) e pelo robô de e-mails push (leitura da caixa). "Testar" pede um token e mostra as permissões do app (não
// lê nem envia e-mail); "Enviar e-mail de teste" manda um e-mail da caixa do sistema.
export default function Microsoft365Config() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const cores = coresDoTema(darkMode);

  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ tenantId: "", clientId: "", caixaSistema: "", segredo: "" });
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [teste, setTeste] = useState(null);
  const [emailTeste, setEmailTeste] = useState("");
  const [enviandoTeste, setEnviandoTeste] = useState(false);

  const carregar = () => {
    get("/microsoft365/config")
      .then((res) => {
        setInfo(res);
        // Tenant ID, Client ID e segredo não voltam do backend (como as chaves da OpenAI): em branco = mantém o gravado
        setForm({ tenantId: "", clientId: "", caixaSistema: res.caixaSistema || "", segredo: "" });
      })
      .catch((e) => errorAlert(e?.message || "Erro ao carregar o Microsoft 365."));
  };

  useEffect(carregar, []);

  const campo = (chave) => (e) => setForm({ ...form, [chave]: e.target.value });

  const salvar = () => {
    setSalvando(true);
    post("/microsoft365/config", form)
      .then(() => {
        successAlert("App do Microsoft 365 salvo. Use \"Testar\" para conferir as permissões.");
        setTeste(null);
        carregar();
      })
      .catch((e) => errorAlert(e?.message || "Erro ao salvar o Microsoft 365."))
      .finally(() => setSalvando(false));
  };

  const testar = () => {
    setTestando(true);
    setTeste(null);
    post("/microsoft365/testar", {})
      .then((res) => setTeste(res))
      .catch((e) => errorAlert(e?.message || "O Microsoft 365 recusou o app."))
      .finally(() => setTestando(false));
  };

  // E-mail de teste da caixa do sistema (a gravada: salve antes)
  const enviarTeste = () => {
    setEnviandoTeste(true);
    post("/microsoft365/testar-envio", { para: emailTeste })
      .then((res) =>
        successAlert(
          `E-mail de teste enviado de ${res.caixa} para ${res.para}` +
            (res.desviado ? " (fora de produção, todo e-mail do sistema vai para esse endereço)" : "") +
            ". Confira a caixa de entrada (e o lixo eletrônico)."
        )
      )
      .catch((e) => errorAlert(e?.message || "O Microsoft 365 não enviou o e-mail de teste."))
      .finally(() => setEnviandoTeste(false));
  };

  // Apagar: nada mais sai por e-mail (sistema, robôs, Mala Direta pelo Microsoft 365) e o robô de push para de ler a caixa
  const apagar = () => {
    infoAviso(
      "Apagar a configuração do Microsoft 365? Até configurar de novo, os e-mails do sistema e dos robôs não são enviados, a conta de saída do tipo Microsoft 365 para e o robô de e-mails push não lê a caixa dele.",
      () => {
        delNoResponse("/microsoft365/config")
          .then(() => {
            successAlert("Configuração do Microsoft 365 apagada.");
            setTeste(null);
            carregar();
          })
          .catch((e) => errorAlert(e?.message || "Erro ao apagar a configuração."));
      }
    );
  };

  // A caixa do sistema não pode ser a da Mala Direta (o backend também recusa)
  const mesmaCaixa = (a, b) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
  const conflito = mesmaCaixa(form.caixaSistema, info?.caixaMalaDireta);

  const rotulo = (texto) => <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>{texto}</InputLabel>;
  const ajuda = (texto) => (
    <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>
      {texto}
    </MDTypography>
  );

  if (!info) {
    return (
      <MDBox display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} color="info" />
      </MDBox>
    );
  }

  return (
    <MDBox sx={{ background: cores.painel, border: `1px solid ${cores.divisoria}`, borderRadius: 2, p: 2 }}>
      <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mb: 2 }}>
        O app registrado no Microsoft 365 do escritório (portal do Azure &gt; App registrations) com que o sistema envia
        e-mails, sem senha de usuário nem SMTP, e com que o robô de e-mails push lê a caixa dele. Para enviar, o app precisa da
        permissão de aplicativo <b>Mail.Send</b> do Microsoft Graph, com o consentimento do admin.
      </MDTypography>
      {!info.configurado && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: "0.85rem" }}>
          Ainda não configurado: os e-mails do sistema e dos robôs não são enviados, a Mala Direta não envia pelo Microsoft 365 e o robô de e-mails push não lê a caixa dele.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          {rotulo("Tenant ID (Directory ID)")}
          <MDInput size="small" fullWidth type="password" value={form.tenantId} onChange={campo("tenantId")} autoComplete="new-password"
            placeholder={info.temTenant ? `Gravado, final ${info.fimTenant || "?"}` : "00000000-0000-0000-0000-000000000000"} />
          {ajuda("Na página do app (Overview): Directory (tenant) ID. Não volta para a tela; em branco, mantém o gravado.")}
        </Grid>
        <Grid item xs={12} md={4}>
          {rotulo("Client ID (Application ID)")}
          <MDInput size="small" fullWidth type="password" value={form.clientId} onChange={campo("clientId")} autoComplete="new-password"
            placeholder={info.temCliente ? `Gravado, final ${info.fimCliente || "?"}` : "00000000-0000-0000-0000-000000000000"} />
          {ajuda("Na página do app (Overview): Application (client) ID. Não volta para a tela; em branco, mantém o gravado.")}
        </Grid>
        <Grid item xs={12} md={4}>
          {rotulo("Segredo do app (Client secret)")}
          <MDInput size="small" fullWidth type="password" value={form.segredo} onChange={campo("segredo")} autoComplete="new-password"
            placeholder={info.temSegredo ? `Gravado, final ${info.fimSegredo || "?"}` : "O Value do segredo"} />
          {ajuda("Certificates & secrets: o Value (não o Secret ID). Fica criptografado e não volta para a tela; em branco, mantém o gravado.")}
        </Grid>
        <Grid item xs={12}>
          <MDTypography variant="button" fontWeight="bold" color="text" display="block" sx={{ px: 1 }}>
            Caixas de envio
          </MDTypography>
          {ajuda("São duas caixas diferentes: cada caixa do Microsoft 365 tem limite diário de envio (10.000 destinatários), e um lote grande da Mala Direta não pode parar a recuperação de senha nem os e-mails dos robôs.")}
        </Grid>
        <Grid item xs={12} md={6}>
          <MDBox sx={{ border: `1px solid ${cores.divisoria}`, borderRadius: 2, p: 1.5, background: cores.cartao, height: "100%" }}>
            {rotulo("E-mails do sistema e dos robôs")}
            <MDInput size="small" fullWidth value={form.caixaSistema} onChange={campo("caixaSistema")} placeholder="sistemas.ral@raadvogados.adv.br"
              autoComplete="off" error={conflito} />
            {conflito ? (
              <MDTypography variant="caption" color="error" display="block" sx={{ px: 1, mt: 0.5 }}>
                Esta é a caixa da Mala Direta. Use outra caixa para os e-mails do sistema.
              </MDTypography>
            ) : (
              ajuda("Recuperação de senha, código de acesso (2FA), avisos, alertas do Monitor de Robôs e os e-mails dos robôs (equipes, relatórios) saem por esta caixa. Gravada com o botão Salvar abaixo.")
            )}
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <MDBox sx={{ border: `1px solid ${cores.divisoria}`, borderRadius: 2, p: 1.5, background: cores.cartao, height: "100%" }}>
            {rotulo("Mala Direta")}
            <MDInput size="small" fullWidth value={info.caixaMalaDireta || "Não configurada"} disabled />
            {ajuda("Os lotes da Mala Direta saem por esta caixa (ou pela caixa do responsável de cada ficha, quando o envio escolhe). Configurada na aba Mala Direta desta tela, com os limites de envio da fila.")}
          </MDBox>
        </Grid>
        <Grid item xs={12}>
          <MDBox display="flex" gap={1} flexWrap="wrap">
            <MDButton variant="contained" color="info" size="medium" sx={{ height: 38 }} onClick={salvar} disabled={salvando || conflito}>
              {salvando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>save</Icon>}
              {salvando ? "Salvando..." : "Salvar"}
            </MDButton>
            <MDButton variant="outlined" color="info" size="medium" sx={{ height: 38 }} onClick={testar} disabled={testando || !info.configurado}>
              {testando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>verified_user</Icon>}
              {testando ? "Testando..." : "Testar"}
            </MDButton>
            {info.configurado && (
              <MDButton variant="text" color="error" size="medium" sx={{ height: 38 }} onClick={apagar}>
                <Icon sx={{ mr: 0.5 }}>delete</Icon>
                Apagar configuração
              </MDButton>
            )}
          </MDBox>
          {ajuda("O teste usa o que está gravado: salve antes. Ele só pede o acesso e lê as permissões do app; nenhum e-mail é lido ou enviado.")}
        </Grid>
        {info.configurado && info.caixaSistema && (
          <Grid item xs={12} md={6}>
            {rotulo("Enviar e-mail de teste da caixa do sistema para")}
            <MDBox display="flex" gap={1}>
              <MDInput size="small" fullWidth value={emailTeste} onChange={(e) => setEmailTeste(e.target.value)} placeholder="seu.email@raadvogados.adv.br" autoComplete="off" />
              <MDButton variant="outlined" color="info" size="medium" sx={{ height: 38, whiteSpace: "nowrap" }} onClick={enviarTeste} disabled={enviandoTeste || !emailTeste}>
                {enviandoTeste ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>send</Icon>}
                {enviandoTeste ? "Enviando..." : "Enviar e-mail de teste"}
              </MDButton>
            </MDBox>
            {ajuda(`Sai de ${info.caixaSistema} pelo Microsoft 365, como os e-mails do sistema e dos robôs.`)}
          </Grid>
        )}
        {teste && (
          <Grid item xs={12}>
            <MDBox sx={{ border: `1px solid ${cores.divisoria}`, borderRadius: 2, p: 1.5, background: cores.cartao }}>
              <MDTypography variant="button" fontWeight="bold" color="text" display="block" mb={1}>
                App {teste.app ? `"${teste.app}"` : ""}: acesso liberado
              </MDTypography>
              <MDBox display="flex" gap={0.75} flexWrap="wrap" mb={1}>
                {(teste.permissoes || []).map((p) => (
                  <Chip key={p} label={p} size="small" variant="outlined" sx={{ color: cores.texto, borderColor: cores.borda }} />
                ))}
                {(teste.permissoes || []).length === 0 && (
                  <MDTypography variant="caption" color="text">Nenhuma permissão de aplicativo.</MDTypography>
                )}
              </MDBox>
              <Alert severity={teste.podeEnviar ? "success" : "warning"} sx={{ mb: 1, fontSize: "0.85rem" }}>
                {teste.podeEnviar
                  ? "Pode enviar (Mail.Send): os e-mails do sistema, dos robôs e da Mala Direta saem pelo Microsoft 365. Confira cada caixa com o seu \"Enviar e-mail de teste\" (o do sistema aqui embaixo; o da Mala Direta na aba Mala Direta)."
                  : "Sem Mail.Send: nenhum e-mail sai por este app (sistema, robôs e Mala Direta). No portal do Azure, o admin adiciona Microsoft Graph > Application permissions > Mail.Send e clica em \"Grant admin consent\"."}
              </Alert>
              <Alert severity={teste.podeLer ? "success" : "info"} sx={{ mb: teste.permissoesAMais?.length ? 1 : 0, fontSize: "0.85rem" }}>
                {teste.podeLer ? "Pode ler caixas (Mail.ReadWrite): atende o robô de e-mails push." : "Sem permissão de leitura de e-mail: o robô de e-mails push não consegue usar este app."}
              </Alert>
              {teste.permissoesAMais?.length > 0 && (
                <Alert severity="warning" sx={{ fontSize: "0.85rem" }}>
                  Permissões que o sistema não usa (vale o admin remover no portal do Azure): <b>{teste.permissoesAMais.join(", ")}</b>.
                </Alert>
              )}
            </MDBox>
          </Grid>
        )}
        <Grid item xs={12}>
          <MDTypography variant="caption" color="text" display="block" sx={{ px: 1 }}>
            Recomendado: o admin limita o app às caixas que ele pode usar (a do push, a do sistema e as da Mala Direta) com uma Application Access
            Policy do Exchange; sem isso, as permissões valem para todas as caixas do escritório. Ao trocar o segredo no Azure, grave o
            novo aqui e apague o antigo lá.
          </MDTypography>
        </Grid>
      </Grid>
    </MDBox>
  );
}
